import { SUPPORT_EMAIL } from '@/constants/common';
import clsx from 'clsx';
import { Button } from '../../buttons';
import { Icon } from '../../Icon';
import { KycReasonNotice } from '../../KycReasonNotice';
import styles from './VerificationFailed.module.css';

interface VerificationFailedProps {
  status: 'general' | 'multipleVerification' | 'rejected';
  // RNB-195: the moderator's reason from Sumsub, and whether this is a duplicate/known-face rejection.
  message?: string | null;
  isDuplicate?: boolean;
}

export const VerificationFailed = ({ status, message, isDuplicate }: VerificationFailedProps) => {
  const icon = status === 'general' ? 'infoCircled' : 'verification';

  const getTitle = () => {
    switch (status) {
      case 'rejected':
        return isDuplicate ? 'This identity is already registered' : 'Verification unsuccessful';
      case 'general':
        return 'Oops.. something goes wrong';
      default:
        return 'Multiple verification attempts';
    }
  };

  const renderDescription = () => {
    switch (status) {
      case 'rejected':
        if (isDuplicate) {
          return (
            <p className={clsx(styles.description, styles.generalDescription)}>
              It looks like this ID already has a RelayPay account. For security reasons we only allow one account per
              person. If you believe this is a mistake, please contact our support team at{' '}
              <a className={styles.link} href={SUPPORT_EMAIL}>
                support@relaypay.io
              </a>
            </p>
          );
        }
        return (
          <>
            <p className={clsx(styles.description, styles.generalDescription)}>
              Your identity verification was unsuccessful and we&apos;re unable to proceed. Please contact our support
              team for further assistance at{' '}
              <a className={styles.link} href={SUPPORT_EMAIL}>
                support@relaypay.io
              </a>
            </p>
            <KycReasonNotice text={message} align="center" className={styles.reasonNotice} />
          </>
        );
      case 'general':
        return (
          <>
            <p className={clsx(styles.description, styles.generalDescription)}>
              We&apos;ve noticed some issues during verification. To solve them, please, reach out to support:
            </p>
            <a className={styles.link} href={SUPPORT_EMAIL}>
              support@relaypay.io
            </a>
          </>
        );
      default:
        return (
          <p className={clsx(styles.description, styles.multipleDescription)}>
            Your verification attempts to require a closer look. Please reach out to our customer support at{' '}
            <a className={styles.link} href={SUPPORT_EMAIL}>
              support@relaypay.io
            </a>{' '}
            to book in a time for manual verification.
          </p>
        );
    }
  };

  const renderIcon = () => {
    switch (status) {
      case 'rejected':
        return 'warning';
      case 'general':
        return 'infoCircled';
      case 'multipleVerification':
        return 'warning';
      default:
        return 'infoCircled';
    }
  };

  return (
    <div className={styles.container}>
      <Icon name={renderIcon()} className={styles.icon} size={64} />
      <h3 className={styles.title}>{getTitle()}</h3>
      {renderDescription()}
      <Button href={SUPPORT_EMAIL} className={styles.button}>
        Write a message
      </Button>
    </div>
  );
};
